import {
  effectivePermissionSetHas,
  MARKETING_REPORT_SECTION_ORDER,
  marketingSectionReadCode,
  type MarketingLeadType,
  type MarketingReportSectionId,
} from '@utazzo/shared';
import { createContext, useContext } from 'react';

/**
 * Which section of the Marketing report is open, split on the lead's Query
 * Type:
 *   - `all`       - every lead together (first in the switch)
 *   - `inorganic` - paid leads, where ad spend and every cost head belong
 *   - `organic`   - leads that came to us on their own; no cost side at all
 *
 * Each section is its own permission (`ops.marketing_reports_<section>.read`),
 * like the Settings tabs, so a role sees only the sections it was granted.
 *
 * A context rather than a prop because every table on the page reads it - the
 * Overview's deferred blocks, the tabs and the full-screen workbook all fetch
 * through the same hooks, which pick the section up from here.
 */
export type MarketingReportSection = MarketingReportSectionId;

export const MarketingLeadTypeContext = createContext<MarketingReportSection>('all');

export const useMarketingLeadType = (): MarketingReportSection =>
  useContext(MarketingLeadTypeContext);

/** The API's `leadType` for a section - none for `all`, which is every lead. */
export const sectionLeadType = (section: MarketingReportSection): MarketingLeadType | null =>
  section === 'all' ? null : section;

export const LEAD_TYPE_LABEL: Record<MarketingReportSection, string> = {
  all: 'All',
  inorganic: 'Inorganic',
  organic: 'Organic',
};

/**
 * Sections the caller may open, in switch order.
 *
 * ADMIN sees every section, mirroring the API guard's bypass. `undefined`
 * permissions means /me has not landed yet - return nothing rather than guess,
 * so a section never flashes in and disappears (and no read fires that the API
 * would refuse). `null` (loaded, no grants) is genuinely empty.
 */
export const visibleSections = (
  effectivePermissions: Set<string> | null | undefined,
  isAdmin: boolean,
): MarketingReportSection[] => {
  if (isAdmin) return [...MARKETING_REPORT_SECTION_ORDER];
  if (!effectivePermissions) return [];
  return MARKETING_REPORT_SECTION_ORDER.filter((section) =>
    effectivePermissionSetHas(effectivePermissions, marketingSectionReadCode(section)),
  );
};

/**
 * The section to open: the one `?type=` asks for when the caller holds it,
 * otherwise the first one they do hold. A link to a section the caller was not
 * granted lands on one they were, instead of on a page of refused reads.
 * Null when they hold none.
 */
export const resolveSection = (
  requested: string | null,
  visible: readonly MarketingReportSection[],
): MarketingReportSection | null => {
  const asked = visible.find((section) => section === requested);
  return asked ?? visible[0] ?? null;
};
